/** Halboffener Bereich [start, end) in einem String. */
export type Range = readonly [number, number];

const FENCE_OPEN = /^( {0,3})(`{3,}|~{3,})(.*)$/;

/**
 * Findet Code-Bereiche (umzäunte Codeblöcke und Inline-Code), in denen
 * Wikilinks, Kommentare und Geheimblöcke nicht interpretiert werden dürfen.
 */
export function findCodeRanges(md: string): Range[] {
  const ranges: Range[] = [];
  const lines = splitLinesWithOffsets(md);
  let gapStart = 0;
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const m = FENCE_OPEN.exec(line.text);
    if (m && !(m[2]![0] === '`' && m[3]!.includes('`'))) {
      const fenceChar = m[2]![0]!;
      const fenceLen = m[2]!.length;
      const close = new RegExp(`^ {0,3}${fenceChar === '`' ? '`' : '~'}{${fenceLen},}[ \\t]*$`);
      let j = i + 1;
      while (j < lines.length && !close.test(lines[j]!.text)) j++;
      const end = j < lines.length ? lines[j]!.end : md.length;
      inlineCode(md, gapStart, line.start, ranges);
      ranges.push([line.start, end]);
      gapStart = end;
      i = j + 1;
      continue;
    }
    i++;
  }
  inlineCode(md, gapStart, md.length, ranges);
  ranges.sort((a, b) => a[0] - b[0]);
  return ranges;
}

/** Inline-Codespannen innerhalb von [from, to); enden spätestens an einer Leerzeile. */
function inlineCode(md: string, from: number, to: number, out: Range[]): void {
  let p = from;
  while (p < to) {
    const tick = md.indexOf('`', p);
    if (tick === -1 || tick >= to) return;
    if (tick > 0 && md[tick - 1] === '\\' && !isEscaped(md, tick - 1)) {
      p = tick + 1;
      continue;
    }
    let n = 0;
    while (md[tick + n] === '`') n++;
    const paraEnd = findParagraphEnd(md, tick + n, to);
    let q = tick + n;
    let found = -1;
    while (q < paraEnd) {
      const t = md.indexOf('`', q);
      if (t === -1 || t >= paraEnd) break;
      let k = 0;
      while (md[t + k] === '`') k++;
      if (k === n) {
        found = t;
        break;
      }
      q = t + k;
    }
    if (found === -1) {
      p = tick + n;
      continue;
    }
    out.push([tick, found + n]);
    p = found + n;
  }
}

function isEscaped(md: string, idx: number): boolean {
  let n = 0;
  for (let i = idx - 1; i >= 0 && md[i] === '\\'; i--) n++;
  return n % 2 === 1;
}

function findParagraphEnd(md: string, from: number, to: number): number {
  const re = /\n[ \t]*\n/g;
  re.lastIndex = from;
  const m = re.exec(md);
  return m && m.index < to ? m.index : to;
}

export interface LineInfo {
  text: string;
  start: number;
  /** Ende inklusive Zeilenumbruch. */
  end: number;
}

export function splitLinesWithOffsets(md: string): LineInfo[] {
  const out: LineInfo[] = [];
  let start = 0;
  while (start <= md.length) {
    const nl = md.indexOf('\n', start);
    if (nl === -1) {
      if (start < md.length) out.push({ text: md.slice(start).replace(/\r$/, ''), start, end: md.length });
      break;
    }
    out.push({ text: md.slice(start, nl).replace(/\r$/, ''), start, end: nl + 1 });
    start = nl + 1;
  }
  return out;
}

/** Prüft, ob eine Position in einem der (sortierten) Bereiche liegt. */
export function inRanges(ranges: readonly Range[], pos: number): boolean {
  let lo = 0;
  let hi = ranges.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const r = ranges[mid]!;
    if (pos < r[0]) hi = mid - 1;
    else if (pos >= r[1]) lo = mid + 1;
    else return true;
  }
  return false;
}
